import {ConsultationsMyAppointmentsQuery} from 'generated/graphql'

interface AppointmentLike {
  scheduledTime?: string | null
  status?: string | null
}

/** Upcoming: SCHEDULED or RESCHEDULED with scheduledTime >= now. */
export const filterUpcomingAppointments = <T extends AppointmentLike>(
  appointments: (T | null)[]
): T[] => {
  const now = new Date()
  return appointments.filter((appointment): appointment is T => {
    if (!appointment) return false
    const status = (appointment.status ?? '').toUpperCase()
    return (
      !!appointment.scheduledTime &&
      new Date(appointment.scheduledTime) >= now &&
      (status === 'SCHEDULED' || status === 'RESCHEDULED')
    )
  })
}

/** Past: COMPLETED or NO_SHOW only. (CANCELLED and future SCHEDULED are excluded.) */
export const filterPastAppointments = <T extends AppointmentLike>(
  appointments: (T | null)[]
): T[] => {
  return appointments.filter((appointment): appointment is T => {
    if (!appointment) return false
    const status = (appointment.status ?? '').toUpperCase()
    return status === 'COMPLETED' || status === 'NO_SHOW'
  })
}

export const formatAppointmentDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const formatAppointmentDayOfWeek = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
  })
}

/** Weekday + day, month, year (e.g. "Wednesday, January 15, 2025"). */
export const formatAppointmentDateWithWeekday = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const formatAppointmentTime = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/** Formats a time range from start time and optional duration in minutes (default 60). */
export const formatAppointmentTimeRange = (
  dateString: string | null | undefined,
  durationMinutes: number = 60
): string => {
  if (!dateString) return ''
  const start = new Date(dateString)
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000)
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }
  return `${start.toLocaleTimeString('en-US', options)} – ${end.toLocaleTimeString('en-US', options)}`
}

export const getAppointmentsBySlug = (
  slug: string,
  appointmentsData?: ConsultationsMyAppointmentsQuery
) => {
  if (!appointmentsData) return []

  const programAppointments =
    appointmentsData?.consultations?.myAppointments?.filter(
      (appointment) => appointment?.programEnrollment?.program?.slug === slug
    )

  return programAppointments ?? []
}

export const getConsultationsBySlug = (
  slug: string,
  appointmentsData?: ConsultationsMyAppointmentsQuery
) => {
  if (!appointmentsData) return []

  const consultationsBySlug =
    appointmentsData?.consultations?.myAppointments?.filter(
      (consultation) =>
        consultation?.consultationEnrollment?.consultation?.slug === slug
    )

  return consultationsBySlug ?? []
}
